import { createRoot } from 'react-dom/client';

import { LicenseInfo } from '@mui/x-license';

import * as Sentry from '@sentry/react';
import { registerSW } from 'virtual:pwa-register';

import '@/app/global-font.css';
import { initI18n } from '@/app/i18n';
import { RootErrorBoundary } from '@/app/ui/ErrorBoundary';
import { clearStaleStorage } from '@/shared/lib/clearStaleStorage';

import App from './App';
import Root from './Root';

try {
  clearStaleStorage();
} catch (error) {
  // Storage blocked (privacy mode, disabled cookies): the app still runs without the reset.
  console.error('Could not clear stale storage', error);
}

initI18n();

if (import.meta.env.VITE_USER_NODE_ENV !== 'local') {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
    sendDefaultPii: true,
    enableLogs: true,
  });
}

if (import.meta.env.VITE_USER_NODE_ENV === 'local' && 'serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then((registrations) => {
    registrations.forEach((registration) => registration.unregister());
  });
}

const container = document.getElementById('root');
LicenseInfo.setLicenseKey(import.meta.env.VITE_MUI_LICENSE);

const SW_UPDATE_INTERVAL_MS = 30 * 60 * 1000;
const SW_UPDATE_MIN_GAP_MS = 5 * 60 * 1000;

if (import.meta.env.VITE_USER_NODE_ENV !== 'local') {
  registerSW({
    immediate: true,
    // SPA route changes never trigger a service-worker update check, so a
    // long-lived tab can keep running a build whose chunks no longer exist on
    // the server. Poll while open and re-check when the tab becomes visible;
    // in autoUpdate mode the page reloads once the new worker activates.
    onRegisteredSW(_swScriptUrl, registration) {
      if (!registration) return;

      let lastCheckAt = Date.now();
      const checkForUpdates = () => {
        lastCheckAt = Date.now();
        registration.update().catch(() => {
          // Offline or a transient network failure — the next check retries.
        });
      };

      setInterval(checkForUpdates, SW_UPDATE_INTERVAL_MS);
      document.addEventListener('visibilitychange', () => {
        if (
          document.visibilityState === 'visible' &&
          Date.now() - lastCheckAt > SW_UPDATE_MIN_GAP_MS
        ) {
          checkForUpdates();
        }
      });
    },
  });
}

if (container) {
  const root = createRoot(container);

  root.render(
    <RootErrorBoundary>
      <Root>
        <App />
      </Root>
    </RootErrorBoundary>,
  );
}
